import { Box, Collapse, Grid } from '@mui/material';
import { FieldArray } from 'react-final-form-arrays';

import {
  Switches,
  CurrencyInputField,
  TextField,
  IconButton,
  Paper,
  Typography,
  AddIcon,
  DeleteIcon,
  ProductGroupSelectField,
  CardContent,
  CardHeader,
  Select,
  Checkboxes,
  AttachMoneyOutlinedIcon,
  IconAvatar,
} from '@occc-app/design-system';
import { parseNumber } from '@occc-app/lib/finalFormUtils';
import { Constraint_Purchase_Threshold_Counting_Attribute } from '@occc/graphql';
import { useCanEditFormFieldsContext } from '@occc-app/App/CanEditFormFieldsContext';

import { PurchaseConstraintFormValues } from './types';

const selectData = [
  { label: 'Money Amount', value: Constraint_Purchase_Threshold_Counting_Attribute.MoneyAmount },
  { label: 'Quantity', value: Constraint_Purchase_Threshold_Counting_Attribute.Quantity },
  { label: 'Selling Unit', value: Constraint_Purchase_Threshold_Counting_Attribute.UnitsCount },
];

type MinimumPurchaseConstraintsProps = {
  values: PurchaseConstraintFormValues;
  allowMultipleFulfillmentsPerProductGroupThreshold?: {
    isEnabled: boolean;
    fieldName: string;
  };
};

export const MinimumPurchaseConstraints = ({
  values,
  allowMultipleFulfillmentsPerProductGroupThreshold,
}: MinimumPurchaseConstraintsProps) => {
  const { isEditable } = useCanEditFormFieldsContext();

  return (
    <Paper>
      <CardHeader
        title={<Typography variant='subtitle1'>Require Minimum Purchase</Typography>}
        subheader={
          <Typography variant='body2' color='textSecondary'>
            Enforce a minimum basket size for availability
          </Typography>
        }
        action={<Switches name='minimumPurchaseConstraints.isEnabled' data={{}} />}
        avatar={<IconAvatar Icon={AttachMoneyOutlinedIcon} />}
        sx={{ py: 2, px: 3 }}
      />
      <Collapse in={values.minimumPurchaseConstraints.isEnabled} timeout='auto' unmountOnExit>
        <CardContent sx={{ pt: 0 }}>
          <FieldArray name='minimumPurchaseConstraints.items'>
            {({ fields }) => {
              const handleAddItem = () => {
                fields.push({
                  countProductsBy: Constraint_Purchase_Threshold_Counting_Attribute.Quantity,
                });
              };
              return (
                <>
                  {fields.map((fieldArrayName, index) => {
                    const countByValue = fields.value[index].countProductsBy;
                    const thresholdName = `${fieldArrayName}.threshold`;
                    const thresholdLabel = 'Threshold';
                    const isCountByItems =
                      countByValue === Constraint_Purchase_Threshold_Counting_Attribute.Quantity ||
                      countByValue === Constraint_Purchase_Threshold_Counting_Attribute.UnitsCount;

                    const handleRemoveItem = () => {
                      fields.remove(index);
                    };

                    const handleSelectChange = () => {
                      fields.update(index, { ...fields.value[index], threshold: undefined });
                    };

                    const isAddIconShown = index === (fields.length || 1) - 1 && isEditable;
                    const isDeleteIconShown = index > 0 && isEditable;

                    return (
                      <Box mb={2} key={fieldArrayName}>
                        <Grid container spacing={2}>
                          <Grid item sm={2}>
                            {isCountByItems && (
                              <TextField
                                name={thresholdName}
                                label={thresholdLabel}
                                fieldProps={{
                                  parse: (value) => parseNumber(value as string),
                                }}
                              />
                            )}
                            {countByValue === 'MONEY_AMOUNT' && (
                              <CurrencyInputField name={thresholdName} label={thresholdLabel} />
                            )}
                          </Grid>
                          <Grid item sm={3}>
                            <Select
                              name={`${fieldArrayName}.countProductsBy`}
                              label='Count By'
                              data={selectData}
                              inputProps={{ onChange: handleSelectChange }}
                              formControlProps={{ size: 'medium', margin: 'none' }}
                            />
                          </Grid>
                          <Grid item sm={5}>
                            <ProductGroupSelectField
                              inputProps={{
                                margin: 'none',
                              }}
                              name={`${fieldArrayName}.eligibleProductGroups`}
                              label='Product groups'
                            />
                          </Grid>
                          <Grid item sm={2}>
                            {isDeleteIconShown && (
                              <IconButton onClick={handleRemoveItem} size='medium'>
                                <DeleteIcon color='error' fontSize='inherit' />
                              </IconButton>
                            )}
                            {isAddIconShown && (
                              <IconButton onClick={handleAddItem} size='medium'>
                                <AddIcon fontSize='inherit' />
                              </IconButton>
                            )}
                          </Grid>
                        </Grid>
                      </Box>
                    );
                  })}
                </>
              );
            }}
          </FieldArray>

          {allowMultipleFulfillmentsPerProductGroupThreshold?.isEnabled && (
            <Checkboxes
              name={allowMultipleFulfillmentsPerProductGroupThreshold.fieldName}
              data={{ label: 'Allow Repeat Fulfillments', value: false }}
            />
          )}
        </CardContent>
      </Collapse>
    </Paper>
  );
};
